import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageLayout, { H2, P, UL } from '../../components/LegalPageLayout';
import { GraduationCap, MapPin, Heart, ShieldCheck, Eye, Compass } from '@phosphor-icons/react';

const FOUNDERS = [
  {
    name: 'Dr. Shivaam Kesarwaani',
    role: 'Co-Founder & Medical Director',
    qualifications: 'MBBS, MS (ENT)',
    specialty: 'ENT, Head & Neck Surgeon · Skull Base Surgery Specialist',
    registration: 'WBMC 72269',
    experience: '10+ Years',
    color: '#4A6750',
    initials: 'SK',
    photo: 'https://customer-assets.emergentagent.com/job_medical-consult-pro/artifacts/nc5lc279_IMG_20250214_203808.png',
    bio: 'Trained in Endoscopic & Skull Base Surgery from Malaysia and Greece, Dr. Shivaam is widely regarded as a Skull Base Surgery Specialist. He handles complex ENT cases involving voice and swallowing disorders, nasal polyps, tumors, and head & neck cancer surgeries with precision and empathy.',
    expertise: [
      'Endoscopic & Skull Base Surgery (international training)',
      'Head & Neck Cancer Surgery',
      'Voice & Swallowing Disorders',
      'Nasal Polyps & Tumors',
    ],
  },
  {
    name: 'Dr. Ankita Mukherjee',
    role: 'Co-Founder & Clinical Director',
    qualifications: 'MBBS, DLO, DNB (ENT)',
    specialty: 'ENT, Head & Neck Surgeon · Pediatric ENT Specialist',
    registration: 'WBMC 78745',
    experience: '10+ Years',
    color: '#D48C70',
    initials: 'AM',
    photo: 'https://customer-assets.emergentagent.com/job_medical-consult-pro/artifacts/a0vn6hem_Untitled%20design%20%283%29.png',
    bio: "Dr. Ankita is a compassionate and highly skilled ENT specialist focused on pediatric ENT, adenoid hypertrophy, chronic ear disorders, and allergic rhinitis. She is especially appreciated for her gentle, patient-friendly approach and her dedication to child and women's ENT care.",
    expertise: [
      'Pediatric ENT',
      'Adenoid Hypertrophy',
      'Chronic Ear Disorders',
      "Allergic Rhinitis & Women's ENT Care",
    ],
  },
];

export default function AboutUs() {
  return (
    <LegalPageLayout title="About SecondDoctorOpinion.com" lastUpdated="May 31, 2026" testId="about-page">
      <P className="text-lg">
        SecondDoctorOpinion.com is an Indian online platform founded by a husband-wife surgeon couple — both
        practicing ENT, Head &amp; Neck Surgeons with over a decade of clinical experience — on a simple
        belief: <strong>every Indian patient deserves a confident, well-informed medical decision</strong>,
        regardless of where they live or how much they can travel.
      </P>

      <H2><Compass size={24} weight="duotone" className="inline mr-2 text-accent" />Our Vision</H2>
      <P>
        To build India's most trusted online second-opinion platform — where every patient, from a metro
        city to the smallest town, can access an honest, evidence-based, ethical opinion from senior verified
        specialists; where over-treatment, unnecessary surgeries, and missed critical diagnoses become rare;
        and where the patient-doctor relationship is grounded in transparency, time, and care.
      </P>

      <H2><Heart size={24} weight="duotone" className="inline mr-2 text-accent" />Our Mission</H2>
      <UL>
        <li><strong>Precise diagnosis first.</strong> Confirm or correct the original diagnosis using complete records and specialist review.</li>
        <li><strong>Right treatment, right time.</strong> Recommend the most appropriate, least-invasive treatment — surgery only when truly necessary.</li>
        <li><strong>Affordable, transparent pricing.</strong> No hidden fees. ₹1,500 Basic and ₹2,500 Premium — a fraction of in-person specialist consultation costs across metros.</li>
        <li><strong>Verified specialists only.</strong> Every doctor MCI/NMC verified, fellowship-trained, minimum 15+ years of practice on average.</li>
        <li><strong>Ethical care across India.</strong> Same standard of clinical excellence whether you are in Delhi, Lucknow, Guwahati, Kolkata, or a rural taluka.</li>
      </UL>

      <H2><Eye size={24} weight="duotone" className="inline mr-2 text-accent" />Why We Built This</H2>
      <P>
        As surgeons, we have personally seen patients undergo major surgeries that could have been avoided
        with a second opinion — and others miss critical diagnoses because they trusted a single perspective.
        Distance, cost, time, and travel often prevent Indians from seeking that crucial second view from a
        senior specialist, especially in tier-2 and tier-3 cities. <strong>SecondDoctorOpinion.com is our
        answer to that gap.</strong> Affordable, accessible, anywhere in India — your records uploaded
        securely, reviewed by a verified senior specialist, opinion delivered in 48 hours.
      </P>

      <H2>Meet the Founders</H2>
      <div className="grid md:grid-cols-2 gap-6 my-6">
        {FOUNDERS.map((f, idx) => (
          <div key={idx} className="border border-primary/10 rounded-lg p-6" data-testid={`founder-card-${idx}`}>
            <div className="flex items-start gap-4 mb-4">
              {f.photo ? (
                <img
                  src={f.photo}
                  alt={f.name}
                  className="w-20 h-20 rounded-full object-cover flex-shrink-0 border-2"
                  style={{ borderColor: f.color }}
                  data-testid={`founder-photo-${idx}`}
                />
              ) : (
                <div
                  className="flex items-center justify-center w-20 h-20 rounded-full font-bold text-white text-xl flex-shrink-0"
                  style={{ backgroundColor: f.color }}
                >
                  {f.initials}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-manrope font-bold text-primary text-lg">{f.name}</p>
                <p className="text-sm font-medium text-accent">{f.role}</p>
                <p className="text-xs text-secondary mt-1">{f.qualifications}</p>
              </div>
            </div>
            <p className="text-sm text-primary font-semibold mb-1">{f.specialty}</p>
            <p className="text-xs text-secondary mb-3">
              <strong>Reg. No.:</strong> {f.registration} · <strong>Experience:</strong> {f.experience}
            </p>
            <p className="text-sm text-secondary leading-relaxed mb-4">{f.bio}</p>
            <div>
              <p className="text-xs uppercase tracking-wider text-secondary font-semibold mb-2">Areas of Expertise</p>
              <ul className="space-y-1">
                {f.expertise.map((e, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-secondary">
                    <GraduationCap size={14} weight="duotone" className="text-primary mt-0.5 flex-shrink-0" />
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <H2>The Couple Behind the Platform</H2>
      <P>
        Dr. Shivaam Kesarwaani and Dr. Ankita Mukherjee are a husband-wife team of practicing ENT, Head &amp;
        Neck Surgeons. They also run <strong>ANKSH ENT &amp; Head Neck Clinic</strong> — a leading ENT clinic
        in Kolkata recognized for skull base surgery, head &amp; neck oncology, and pediatric ENT care, with
        over 5,000 satisfied patients and a 95% satisfaction rate. Their combined clinical experience,
        international surgical training (Malaysia, Greece), and lived understanding of Indian patients'
        healthcare journey form the foundation of SecondDoctorOpinion.com.
      </P>

      <H2>What We Do</H2>
      <UL>
        <li>Match your case with a specialist registered with the NMC or a State Medical Council in the relevant specialty (39 specialties covered)</li>
        <li>Provide structured written opinions addressing: diagnosis confirmation, alternative treatments, surgery necessity, risk-benefit analysis, prognosis, and follow-up plan</li>
        <li>Offer live video consultations with verified specialists across India</li>
        <li>Use Claude Sonnet 4.5 AI to assist specialists with preliminary analyses (always reviewed and verified by the human specialist)</li>
        <li>Handle your medical data under India's Digital Personal Data Protection Act, 2023</li>
      </UL>

      <H2><ShieldCheck size={24} weight="duotone" className="inline mr-2 text-accent" />When a Second Opinion Can Help</H2>
      <UL>
        <li>Before a major or irreversible surgery, to check whether it is needed now and what alternatives exist</li>
        <li>When a diagnosis is unclear or two reports disagree</li>
        <li>For rare conditions that benefit from subspecialty review</li>
        <li>To avoid travelling to another city just to get a second view</li>
      </UL>

      <H2><MapPin size={24} weight="duotone" className="inline mr-2 text-accent" />Get in Touch</H2>
      <P>
        WhatsApp: <strong>+91 88408 04798</strong><br />
        Email: <strong>support@seconddoctoropinion.com</strong><br />
        Hours: Monday – Saturday, 9 AM – 9 PM IST
      </P>

      <H2>Are You a Specialist?</H2>
      <P>
        Join our panel of expert specialists across India. Earn meaningfully reviewing well-documented cases
        on your own schedule.{' '}
        <Link to="/doctors/apply" className="text-primary font-semibold hover:underline">Apply to join →</Link>
      </P>
    </LegalPageLayout>
  );
}
